import { useCallback, useMemo } from 'react';

import { useMounted } from '@kc-console/core';

import { TOKEN_SCOPE } from '../constants';
import { flatSubMenus, normalizePath } from '../utils';
import { useRouter } from '.';
import useMegaMenuQuery from './useMegaMenuQuery';
import useServiceAppState from './useServiceAppState';

import type { TokenScope } from '../constants';
/**
 * 스코프 기반 네비게이션을 처리하는 훅입니다.
 *
 * @description
 * - 현재 토큰 스코프와 대상 경로의 스코프를 비교
 * - 스코프에 따른 적절한 URL 생성 (프로젝트/도메인 스코프)
 * - 스코프가 다른 경우 전체 새로고침하며 이동하고 같을 경우, 패스 이동
 *
 * @example
 * ```tsx
 * const { goToScopedPath, getScopedPath } = useScopedNavigation();
 *
 * // 스코프에 따른 네비게이션
 * goToScopedPath('/vpc/instances');
 *
 * // 스코프가 적용된 URL만 생성
 * const url = getScopedPath('/vpc/instances');
 * ```
 *
 * @returns {Object} 스코프 기반 네비게이션 관련 함수들
 * @returns {Function} returns.getIsSameScopeWithCurrent - 현재 스코프와 대상 스코프 비교
 * @returns {Function} returns.getScopedPath - 스코프가 적용된 URL 생성
 * @returns {Function} returns.getTargetScope - 경로에 해당하는 대상 스코프 조회
 * @returns {Function} returns.goToScopedPath - 스코프에 따른 네비게이션 실행
 */
const useScopedNavigation = () => {
  const { push } = useRouter();
  const mounted = useMounted();
  const {
    state: { scope: currentScope, region: currentRegion, projectId, domainId },
  } = useServiceAppState();
  const { data: categories = [] } = useMegaMenuQuery();

  // 전체 메뉴 데이터 (각 메뉴의 path 가 어떠한 scope 인지 확인하기 위한 데이터)
  const allMenu = useMemo(() => {
    const services = categories.flatMap(category => category.services);
    return services.flatMap(service => {
      const menuItems = flatSubMenus(service.menus);

      return [service, ...menuItems.sort((a, b) => a.path.length - b.path.length)];
    });
  }, [categories]);

  /**
   * 주어진 경로에 해당하는 대상 스코프를 조회합니다.
   *
   * @param path - 조회할 경로
   * @returns 해당 경로의 토큰 스코프, 기본값은 PROJECT
   */
  const getTargetScope = useCallback(
    (path: string): TokenScope => {
      const normalizedPath = normalizePath(path);
      const targetMenu = allMenu.find(menu => normalizedPath === menu.path);
      return targetMenu?.scope ?? TOKEN_SCOPE.PROJECT;
    },
    [allMenu],
  );

  /**
   * 현재 스코프와 대상 스코프가 동일한지 확인합니다.
   *
   * @param scope - 비교할 대상 스코프
   * @returns 스코프가 동일한 경우 true
   */
  const getIsSameScopeWithCurrent = useCallback(
    (scope: TokenScope): boolean => currentScope === scope,
    [currentScope],
  );

  /**
   * 스코프에 따른 적절한 URL을 생성합니다.
   *
   * @param path - 대상 경로
   * @returns 스코프가 적용된 URL
   */
  const getScopedPath = useCallback(
    (path: string, scope?: TokenScope): string => {
      const normalizedPath = normalizePath(path);
      const targetScope = scope || getTargetScope(normalizedPath);
      const queryParams = new URLSearchParams();

      if (targetScope === TOKEN_SCOPE.PROJECT) {
        if (projectId) queryParams.set('project_id', projectId);
      } else {
        if (domainId) queryParams.set('domain_id', domainId);
      }

      if (currentRegion) queryParams.set('region', currentRegion);

      const scopedPathWithQuery = queryParams.toString()
        ? `${normalizedPath}?${queryParams.toString()}`
        : normalizedPath;

      // SSR과 클라이언트 첫 렌더링에서는 항상 상대 경로 반환 (hydration mismatch 방지)
      // 마운트 후에는 현재 스코프와 동일한 경우 원본 경로 반환
      if (!mounted || getIsSameScopeWithCurrent(targetScope)) return scopedPathWithQuery;

      // 스코프에 따른 URL 생성
      const baseUrl = window.location.origin;

      return `${baseUrl}${scopedPathWithQuery}`;
    },
    [getTargetScope, getIsSameScopeWithCurrent, projectId, domainId, currentRegion, mounted],
  );

  /**
   * 스코프에 따른 네비게이션을 실행합니다.
   *
   * @deprecated `useScopedLinkBuilder().getProps()` 로 대체. ctrl/cmd+click 새 탭 등
   *   브라우저 네이티브 동작을 막으므로 클릭 핸들러에서 사용 금지.
   *   ```tsx
   *   const { getProps } = useScopedLinkBuilder();
   *   <a {...getProps(path)}>{name}</a>
   *   ```
   *   anchor 불가 / 조건부 full reload 상세: `docs/docs/hosts/navigation.md`
   * @param path - 이동할 경로
   */
  const goToScopedPath = useCallback(
    (path: string, scope?: TokenScope): void => {
      const normalizedPath = normalizePath(path);
      const href = getScopedPath(normalizedPath);
      const targetScope = scope || getTargetScope(normalizedPath);

      if (getIsSameScopeWithCurrent(targetScope)) {
        push(href);
      } else {
        window.location.href = href;
      }
    },
    [getScopedPath, getTargetScope, getIsSameScopeWithCurrent, push],
  );

  return {
    getIsSameScopeWithCurrent,
    getScopedPath,
    getTargetScope,
    goToScopedPath,
  };
};

export default useScopedNavigation;
